import { createContext, useContext, useEffect, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { useAuth } from "@/hooks/useAuth";
import { db } from "@/lib/firebase";
import { signOut } from "@/lib/auth";
import { supportCall, type SupportBootstrap } from "@/lib/supportPortal";
import { supportErrorMessage } from "@/lib/supportCallables";
import { ForcePasswordChangeGate } from "@/components/auth/ForcePasswordChangeGate";
import { ImpersonationBanner } from "@/components/layout/ImpersonationBanner";
import { ChangePasswordSection } from "@/components/auth/ChangePasswordSection";

const PortalContext = createContext<SupportBootstrap | null>(null);
// eslint-disable-next-line react-refresh/only-export-components
export function useSupportPortal() { return useContext(PortalContext); }

export function SupportPortalShell() {
  const { user, orgUser, realRole, isImpersonating } = useAuth();
  const orgId = orgUser?.orgId;
  const [state, setState] = useState<{ key: string; data: SupportBootstrap | null; error: string | null } | null>(null);
  const key = `${user?.uid}:${orgId}:${realRole}:${isImpersonating}`;
  useEffect(() => {
    let cancelled = false;
    let stop: (() => void) | undefined;
    let generation = 0;
    const deadlines = new Set<number>();
    const revoke = (message: string) => {
      generation++;
      stop?.();
      stop = undefined;
      if (!cancelled) setState({ key, data: null, error: message });
    };
    const load = async () => {
      const requestGeneration = ++generation;
      const deadline = window.setTimeout(() => {
        deadlines.delete(deadline);
        if (!cancelled && requestGeneration === generation) revoke("Support access could not be verified. Reconnect and try again.");
      }, 10_000);
      deadlines.add(deadline);
      try {
        const data = await supportCall<SupportBootstrap>(false, "bootstrap", isImpersonating ? { orgId } : {});
        if (cancelled || requestGeneration !== generation) return;
        if (!data.supportEnabled || data.org.id !== orgId) throw new Error("Support is not enabled for this organization.");
        setState({ key, data, error: null });
        if (!stop) {
          let live = true;
          const unsubscribe = onSnapshot(doc(db, "supportClientOrgs", orgId!), (snap) => {
            if (cancelled || !live) return;
            if (!snap.exists() || snap.data().supportEnabled !== true || snap.data().orgId !== orgId) revoke("Support is unavailable.");
          }, () => { if (!cancelled && live) revoke("Support access is no longer available."); });
          stop = () => { live = false; unsubscribe(); };
        }
      } catch (err) {
        if (!cancelled && requestGeneration === generation) revoke(supportErrorMessage(err));
      } finally {
        window.clearTimeout(deadline);
        deadlines.delete(deadline);
      }
    };
    void load();
    const refresh = () => { if (document.visibilityState !== "hidden") void load(); };
    // A dependency-only rules change need not produce a document snapshot promptly.
    // Revalidate through the authoritative callable even when the safe org document is unchanged.
    const timer = window.setInterval(refresh, 30_000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => { cancelled = true; generation++; stop?.(); window.clearInterval(timer); deadlines.forEach((deadline) => window.clearTimeout(deadline)); window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); };
  }, [key, orgId, isImpersonating]);
  const current = state?.key === key ? state : null;
  return <div className="min-h-screen bg-sidebar text-text">
    <ImpersonationBanner />
    <header className="border-b border-border bg-white px-6 py-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div><h1 className="font-heading text-xl font-bold">{current?.data?.org.name ?? "BiG"} Support</h1><p className="text-sm text-text-muted">{orgUser?.email}</p></div>
        <nav className="flex flex-wrap items-center gap-4 text-sm" aria-label="Support navigation">
          {current?.data && <><NavLink to="/org/support" end>Tickets</NavLink><NavLink to="/org/support/new">New request</NavLink>
            {realRole !== "ORG_REQUESTER" && <><NavLink to="/org/support/usage">Terms and usage</NavLink><NavLink to="/org/support/requesters">Requesters</NavLink><Link to="/org">Organization</Link></>}</>}
          <NavLink to="/org/support/account">Account</NavLink><button onClick={() => void signOut()}>Sign out</button>
        </nav>
      </div>
    </header>
    <main className="mx-auto max-w-6xl p-6">
      {!current ? <p role="status">Loading support...</p> : current.error ? <p role="alert">{current.error}</p> :
        <PortalContext.Provider value={current.data}><ForcePasswordChangeGate><Outlet /></ForcePasswordChangeGate></PortalContext.Provider>}
    </main>
  </div>;
}

export function SupportAccountPage() {
  return <div className="max-w-lg"><h2 className="mb-4 font-heading text-xl font-bold">Your account</h2><ChangePasswordSection /></div>;
}
